import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import filesService from '../../services/filesService';
import folderService from '../../services/folderService';
import PageHeader from '../shared/PageHeader';
import PageLoader from '../shared/PageLoader';
import {
  Typography,
  Grid,
  Chip,
  Button,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TablePagination,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  RefreshCw,
  Filter,
  Search,
  Eye,
  Trash2,
  Settings,
  Upload,
  FolderOpen,
  ArrowLeft,
  Info,
} from 'lucide-react';
import styles from './KnowledgeBaseView.module.scss';
import FileDeleteDialog from './FileDeleteDialog';
import hubStyles from '../KnowledgeBaseHub/KnowledgeBaseHub.module.scss';
import ResourceShareDialog from '../shared/ResourceShareDialog';
import ManageSharingDialog from '../shared/ManageSharingDialog';

const FolderFilesView = () => {
  const { isDarkMode } = useTheme();
  const { has, ready: permissionsReady } = usePermissions();
  const canShareResources = permissionsReady && has(PERMISSIONS.RESOURCE_SHARING);
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isShared = searchParams.get('shared') === 'true';
  const canModifyFiles = !isShared || searchParams.get('permission') === 'write';

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#e36250 !important',
      },
      secondary: {
        main: '#8b5cf6',
      },
    },
  });

  const [files, setFiles] = useState([]);
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [folder, setFolder] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedFile, setSelectedFile] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [manageSharingOpen, setManageSharingOpen] = useState(false);

  const [stats, setStats] = useState({
    total_files: 0,
    pending_files: 0,
    processing_files: 0,
    completed_files: 0,
    failed_files: 0,
    total_filters: 0,
  });

  const loadData = useCallback(
    /** @param {{ silent?: boolean }} [opts] */
    async (opts = {}) => {
      const silent = !!opts.silent;
      if (!silent) setLoading(true);
      try {
        const folderWithFiles = await folderService.getFolderWithFiles(folderId);

        const filesList = folderWithFiles.files || [];
        const folderData = folderWithFiles.folder || {};

        setFolder(folderData);
        setFiles(filesList);

        setStats({
          total_files: folderWithFiles.file_count || filesList.length,
          pending_files: filesList.filter(
            (f) => (f.processing_status || f.processingStatus) === 'pending'
          ).length,
          processing_files: filesList.filter(
            (f) => (f.processing_status || f.processingStatus) === 'processing'
          ).length,
          completed_files: filesList.filter(
            (f) => (f.processing_status || f.processingStatus) === 'completed'
          ).length,
          failed_files: filesList.filter(
            (f) => (f.processing_status || f.processingStatus) === 'failed'
          ).length,
          total_filters: 0,
        });

        setFilters([]);
      } catch (error) {
        console.error('Error loading data:', error);

        if (!silent) {
          setFiles([]);
          setFilters([]);
          setStats({
            total_files: 0,
            pending_files: 0,
            processing_files: 0,
            completed_files: 0,
            failed_files: 0,
            total_filters: 0,
          });

          if (error.message.includes('Failed to fetch')) {
            showToast('API service not available. Showing empty state.', 'warning');
          } else {
            showToast('Failed to load data', 'error');
          }
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [folderId]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const hasProcessing = files.some(
      (f) => (f.processing_status || f.processingStatus) === 'processing'
    );
    if (!hasProcessing || !folderId) return undefined;

    const id = window.setInterval(() => {
      loadData({ silent: true });
    }, 20000);

    return () => clearInterval(id);
  }, [files, folderId, loadData]);

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleToastClose = () => {
    setToast({ open: false, message: '', severity: 'info' });
  };

  const filteredFiles = files.filter((file) => {
    const processingStatus = file.processing_status || file.processingStatus;
    const primaryTag = file.primary_tag || file.primaryTag;
    const secondaryTags = file.secondary_tags || file.secondaryTags || '';
    const fileName = file.file_name || file.fileName;

    const matchesStatus = statusFilter === 'all' || processingStatus === statusFilter;
    const matchesTag =
      tagFilter === 'all' ||
      primaryTag === tagFilter ||
      String(secondaryTags).includes(tagFilter);
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      searchQuery === '' ||
      (fileName && String(fileName).toLowerCase().includes(q)) ||
      (primaryTag && String(primaryTag).toLowerCase().includes(q)) ||
      (secondaryTags && String(secondaryTags).toLowerCase().includes(q));

    return matchesStatus && matchesTag && matchesSearch;
  });

  const uniqueTags = [
    ...new Set([
      ...files.map((f) => f.primary_tag || f.primaryTag).filter(Boolean),
      ...files.flatMap((f) =>
        (f.secondary_tags || f.secondaryTags || '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      ),
    ]),
  ];

  const getStatusIcon = (status) => {
    const iconProps = { size: 20, strokeWidth: 2 };
    switch (status) {
      case 'completed':
        return <CheckCircle2 {...iconProps} color="#16a34a" aria-hidden />;
      case 'failed':
        return <XCircle {...iconProps} color="#dc2626" aria-hidden />;
      case 'processing':
        return <Loader2 {...iconProps} color="#ca8a04" className={styles.spin} aria-hidden />;
      case 'pending':
        return <Clock {...iconProps} color="#6b7280" aria-hidden />;
      default:
        return <Info {...iconProps} color="#3b82f6" aria-hidden />;
    }
  };

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'completed':
        return hubStyles.folderChipOk;
      case 'failed':
        return styles.filesStatDangerChip;
      case 'processing':
        return styles.filesStatWarnChip;
      case 'pending':
        return hubStyles.folderChip;
      default:
        return hubStyles.folderChipAccent;
    }
  };

  const handleViewDetails = (file) => {
    setSelectedFile(file);
    setDetailsDialogOpen(true);
  };

  const handleViewFilter = (filterId) => {
    const filter = filters.find((f) => f.id === filterId);
    setSelectedFilter(filter);
    setFilterDialogOpen(true);
  };

  const handleRetryFile = async (fileId) => {
    try {
      await filesService.updateFileStatus(fileId, 'pending');
      showToast('File queued for retry', 'success');
      loadData();
    } catch (error) {
      showToast('Failed to retry file', 'error');
    }
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;
    
    setIsDeleting(true);
    try {
      await filesService.deleteFile(fileToDelete.id);
      showToast('File deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      loadData();
    } catch (error) {
      showToast('Failed to delete file', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatProcessingTime = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    if (!endTime) return 'In progress...';

    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);

    if (diffMins > 0) {
      return `${diffMins}m ${diffSecs % 60}s`;
    }
    return `${diffSecs}s`;
  };

  const handleUploadFiles = () => {
    navigate(`/knowledge-base/folder/${folderId}/upload`);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setTagFilter('all');
    setSearchQuery('');
  };

  const uploadAction = canModifyFiles ? (
    <button type="button" className={hubStyles.uploadBtn} onClick={handleUploadFiles}>
      <Upload size={18} strokeWidth={2} aria-hidden />
      Upload New Files
    </button>
  ) : null;
  const headerActions = (
    <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {uploadAction}
      {!isShared && canShareResources && <Button size="small" variant="outlined" onClick={() => setShareOpen(true)}>Share</Button>}
      {!isShared && canShareResources && <Button size="small" variant="outlined" onClick={() => setManageSharingOpen(true)}>Manage sharing</Button>}
    </span>
  );

  const statItems = [
    { label: 'Total', value: stats.total_files, color: '#3b82f6' },
    { label: 'Pending', value: stats.pending_files, color: '#6b7280' },
    { label: 'Processing', value: stats.processing_files, color: '#ca8a04' },
    { label: 'Completed', value: stats.completed_files, color: '#16a34a' },
    { label: 'Failed', value: stats.failed_files, color: '#dc2626' },
    { label: 'Configurations', value: stats.total_filters, color: '#8b5cf6' },
  ];

  const paginatedFiles = filteredFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <ThemeProvider theme={muiTheme}>
        <div className={styles.knowledgeBaseView}>
          <PageHeader
            title={`Files in ${folder?.name || 'Folder'}`}
            subtitle="View and manage files in this knowledge base folder"
            backButton={<ArrowLeft size={22} strokeWidth={2} />}
            onBackClick={() => navigate('/knowledge-hub?tab=d1')}
            actionButton={headerActions}
          />
          <PageLoader
            message={`Loading files in ${folder?.name || 'Folder'}...`}
            size={50}
            showOverlay={true}
          />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={styles.knowledgeBaseView}>
        <PageHeader
          title={`Files in ${folder?.name || 'Folder'}`}
          subtitle="View and manage files in this knowledge base folder"
          backButton={<ArrowLeft size={22} strokeWidth={2} />}
          onBackClick={() => navigate('/knowledge-hub?tab=d1')}
          actionButton={headerActions}
        />

        <div className={styles.filesStatsStrip}>
          {statItems.map((s) => (
            <div key={s.label} className={styles.filesStatCard}>
              <p className={styles.filesStatValue} style={{ color: s.color }}>
                {s.value}
              </p>
              <p className={styles.filesStatLabel}>{s.label}</p>
            </div>
          ))}
        </div>

        <div className={hubStyles.listCard}>
          <div className={hubStyles.folderPanelToolbar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <Filter size={18} strokeWidth={2} style={{ color: 'var(--primary)', flexShrink: 0 }} aria-hidden />
              <p className={hubStyles.listTitle} style={{ margin: 0 }}>
                Filters &amp; search
              </p>
            </div>
            <div className={hubStyles.folderToolbarRight}>
              <button
                type="button"
                className={hubStyles.iconBtn}
                onClick={loadData}
                title="Refresh"
                aria-label="Refresh file list"
              >
                <RefreshCw size={18} strokeWidth={2} />
              </button>
            </div>
          </div>
          <div className={styles.filesFiltersBody}>
            <div className={hubStyles.searchWrap}>
              <Search className={hubStyles.searchIcon} size={16} strokeWidth={2} aria-hidden />
              <input
                type="search"
                className={hubStyles.searchInput}
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search files"
              />
            </div>
            <select
              className={styles.filesNativeSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
            <select
              className={styles.filesNativeSelect}
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              aria-label="Filter by tag"
            >
              <option value="all">All tags</option>
              {uniqueTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`${hubStyles.viewAgentBtn} ${styles.filesClearBtn}`}
              onClick={clearFilters}
            >
              Clear filters
            </button>
          </div>
        </div>

        <div className={hubStyles.listCard}>
          {filteredFiles.length === 0 && !loading ? (
            <div className={hubStyles.emptyState}>
              <div className={hubStyles.emptyIcon} aria-hidden>
                <FolderOpen size={28} strokeWidth={1.75} />
              </div>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 8px' }}>
                No files found in this folder
              </p>
              <p style={{ margin: '0 0 20px', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
                {files.length === 0
                  ? "This folder doesn't contain any files yet. Upload your first file to get started."
                  : 'No files match your current filters. Try adjusting your search criteria.'}
              </p>
              {canModifyFiles && <button type="button" className={hubStyles.uploadBtn} onClick={handleUploadFiles}>
                <Upload size={18} strokeWidth={2} aria-hidden />
                Upload files
              </button>}
            </div>
          ) : (
            <>
              <div className={hubStyles.listHeader}>
                <div className={hubStyles.listHeaderLeft}>
                  <p className={hubStyles.listTitle}>Files in folder</p>
                  <span className={hubStyles.countLabel}>{filteredFiles.length} shown</span>
                </div>
              </div>
              {paginatedFiles.map((file) => {
                const proc = file.processing_status || file.processingStatus;
                const fname = file.file_name || file.fileName;
                const fid = file.file_id || file.fileId;
                return (
                  <div key={file.id} className={hubStyles.fileRow} style={{ cursor: 'default' }}>
                    <div className={hubStyles.fileRowMain}>
                      <div className={`${hubStyles.typeIconWrap} ${hubStyles.typeDoc}`}>
                        <FileText size={20} strokeWidth={2} aria-hidden />
                      </div>
                      <div className={hubStyles.fileMeta}>
                        <h3>{fname}</h3>
                        <p className="desc">ID: {fid}</p>
                        <div className="subRow">
                          <span className="tag">{file.file_size || file.fileSize}</span>
                          <span>·</span>
                          <span>{formatDate(file.created_at || file.createdAt)}</span>
                        </div>
                        {proc === 'failed' && (file.error_message || file.errorMessage) && (
                          <p
                            className="desc"
                            style={{ color: '#dc2626', marginTop: 4, whiteSpace: 'normal' }}
                            title={file.error_message || file.errorMessage}
                          >
                            {file.error_message || file.errorMessage}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className={styles.fileRowBlock}>
                      <div className={hubStyles.folderMetaChips}>
                        {(file.primary_tag || file.primaryTag) && (
                          <span className={`${hubStyles.folderChip} ${hubStyles.folderChipAccent}`}>
                            {file.primary_tag || file.primaryTag}
                          </span>
                        )}
                        {(file.secondary_tags || file.secondaryTags || '')
                          .split(',')
                          .map((t) => t.trim())
                          .filter(Boolean)
                          .map((tag) => (
                            <span key={tag} className={hubStyles.folderChip}>
                              {tag}
                            </span>
                          ))}
                      </div>
                      <div className={styles.fileRowMetaLine}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {getStatusIcon(proc)}
                          <span className={`${hubStyles.folderChip} ${getStatusChipClass(proc)}`}>
                            {proc}
                          </span>
                        </span>
                        <span className={`${hubStyles.folderChip} ${file.vectorized ? hubStyles.folderChipOk : hubStyles.folderChip}`}>
                          V: {file.vectorized ? '✓' : '✗'}
                        </span>
                        <span className={`${hubStyles.folderChip} ${file.summarized ? hubStyles.folderChipOk : hubStyles.folderChip}`}>
                          S: {file.summarized ? '✓' : '✗'}
                        </span>
                        <span className={`${hubStyles.folderChip} ${file.preserved ? hubStyles.folderChipOk : hubStyles.folderChip}`}>
                          P: {file.preserved ? '✓' : '✗'}
                        </span>
                        <span>
                          {formatProcessingTime(
                            file.processing_started_at || file.processingStartedAt,
                            file.processing_completed_at || file.processingCompletedAt
                          )}
                        </span>
                      </div>
                    </div>
                    <div className={`${hubStyles.rowActions} ${styles.fileFolderRowActions}`}>
                      <Tooltip title="View details">
                        <button
                          type="button"
                          className={hubStyles.iconBtn}
                          onClick={() => handleViewDetails(file)}
                          aria-label="View file details"
                        >
                          <Eye size={18} strokeWidth={2} />
                        </button>
                      </Tooltip>
                      <Tooltip title="View configuration">
                        <button
                          type="button"
                          className={hubStyles.iconBtn}
                          onClick={() => handleViewFilter(file.filter_id)}
                          aria-label="View processing configuration"
                        >
                          <Settings size={18} strokeWidth={2} />
                        </button>
                      </Tooltip>
                      {canModifyFiles && proc === 'failed' && (
                        <Tooltip title="Retry processing">
                          <button
                            type="button"
                            className={hubStyles.iconBtn}
                            onClick={() => handleRetryFile(file.id)}
                            aria-label="Retry processing"
                          >
                            <RefreshCw size={18} strokeWidth={2} />
                          </button>
                        </Tooltip>
                      )}
                      {canModifyFiles && <Tooltip title="Delete file">
                        <button
                          type="button"
                          className={`${hubStyles.iconBtn} ${styles.fileDeleteBtn}`}
                          onClick={() => handleDeleteClick(file)}
                          aria-label="Delete file"
                        >
                          <Trash2 size={18} strokeWidth={2} />
                        </button>
                      </Tooltip>}
                    </div>
                  </div>
                );
              })}
              {filteredFiles.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredFiles.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              )}
            </>
          )}
        </div>

        <Dialog
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">File Details</Typography>
              <IconButton onClick={() => setDetailsDialogOpen(false)} size="small" aria-label="Close">
                <Close />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent>
            {selectedFile && (
              <div>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Basic Information
                    </Typography>
                    <div style={{ marginBottom: 16 }}>
                      <Typography variant="body2">
                        <strong>File Name:</strong> {selectedFile.file_name || selectedFile.fileName}
                      </Typography>
                      <Typography variant="body2">
                        <strong>File ID:</strong> {selectedFile.file_id || selectedFile.fileId}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Size:</strong> {selectedFile.file_size || selectedFile.fileSize}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Created:</strong>{' '}
                        {formatDate(selectedFile.created_at || selectedFile.createdAt)}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Processing Status
                    </Typography>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        {getStatusIcon(selectedFile.processing_status || selectedFile.processingStatus)}
                        <Typography variant="body2" sx={{ ml: 1, textTransform: 'capitalize' }}>
                          <strong>Status:</strong>{' '}
                          {selectedFile.processing_status || selectedFile.processingStatus}
                        </Typography>
                      </div>
                      {(selectedFile.processing_started_at || selectedFile.processingStartedAt) && (
                        <Typography variant="body2">
                          <strong>Started:</strong>{' '}
                          {formatDate(
                            selectedFile.processing_started_at || selectedFile.processingStartedAt
                          )}
                        </Typography>
                      )}
                      {(selectedFile.processing_completed_at || selectedFile.processingCompletedAt) && (
                        <Typography variant="body2">
                          <strong>Completed:</strong>{' '}
                          {formatDate(
                            selectedFile.processing_completed_at || selectedFile.processingCompletedAt
                          )}
                        </Typography>
                      )}
                      {(selectedFile.error_message || selectedFile.errorMessage) && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {selectedFile.error_message || selectedFile.errorMessage}
                        </Alert>
                      )}
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Tags &amp; Classification
                    </Typography>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                        <Chip
                          label={`Primary: ${selectedFile.primary_tag || selectedFile.primaryTag}`}
                          color="primary"
                          size="small"
                        />
                        {(selectedFile.secondary_tags || selectedFile.secondaryTags || '')
                          .split(',')
                          .filter((tag) => tag.trim())
                          .map((tag, index) => (
                            <Chip
                              key={index}
                              label={`Secondary: ${tag.trim()}`}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Processing Configuration
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Paper
                          sx={{
                            p: 1,
                            textAlign: 'center',
                            backgroundColor: selectedFile.vectorized ? '#e8f5e8' : '#ffebee',
                          }}
                        >
                          <Typography variant="caption" display="block">
                            Vectorized
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ color: selectedFile.vectorized ? '#4caf50' : '#f44336' }}
                          >
                            {selectedFile.vectorized ? '✓' : '✗'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper
                          sx={{
                            p: 1,
                            textAlign: 'center',
                            backgroundColor: selectedFile.summarized ? '#e8f5e8' : '#ffebee',
                          }}
                        >
                          <Typography variant="caption" display="block">
                            Summarized
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ color: selectedFile.summarized ? '#4caf50' : '#f44336' }}
                          >
                            {selectedFile.summarized ? '✓' : '✗'}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper
                          sx={{
                            p: 1,
                            textAlign: 'center',
                            backgroundColor: selectedFile.preserved ? '#e8f5e8' : '#ffebee',
                          }}
                        >
                          <Typography variant="caption" display="block">
                            Preserved
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ color: selectedFile.preserved ? '#4caf50' : '#f44336' }}
                          >
                            {selectedFile.preserved ? '✓' : '✗'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Processing Configuration</Typography>
              <IconButton onClick={() => setFilterDialogOpen(false)} size="small" aria-label="Close">
                <Close />
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent>
            {selectedFilter && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Chunking Settings
                  </Typography>
                  <div style={{ marginBottom: 16 }}>
                    <Typography variant="body2">
                      <strong>Strategy:</strong>{' '}
                      {selectedFilter.chunkingStrategy || selectedFilter.chunking_strategy}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Chunk Size:</strong>{' '}
                      {selectedFilter.chunkSize || selectedFilter.chunk_size}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Overlap:</strong> {selectedFilter.overlap}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Data Size:</strong>{' '}
                      {selectedFilter.dataSize || selectedFilter.data_size}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    AI &amp; Processing
                  </Typography>
                  <div style={{ marginBottom: 16 }}>
                    <Typography variant="body2">
                      <strong>Embedding Model:</strong>{' '}
                      {selectedFilter.embeddingModel || selectedFilter.embedding_model}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Search Type:</strong>{' '}
                      {selectedFilter.searchType || selectedFilter.search_type}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Batch Mode:</strong>{' '}
                      {(selectedFilter.useBatchMode !== undefined
                        ? selectedFilter.useBatchMode
                        : selectedFilter.use_batch)
                        ? 'Enabled'
                        : 'Disabled'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created:</strong>{' '}
                      {formatDate(selectedFilter.created_at || selectedFilter.createdAt)}
                    </Typography>
                  </div>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFilterDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <FileDeleteDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setFileToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          file={fileToDelete}
          isDeleting={isDeleting}
        />
        <ResourceShareDialog
          open={shareOpen}
          resource={folder ? { type: 'folder_dim1', id: folderId, name: folder.name } : null}
          onClose={() => setShareOpen(false)}
          onSuccess={(result) => {
            const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
            showToast(warnings.length ? `Folder shared. ${warnings.join(' ')}` : 'Folder shared successfully.', warnings.length ? 'warning' : 'success');
          }}
        />
        <ManageSharingDialog
          open={manageSharingOpen}
          resource={folder ? { type: 'folder_dim1', id: folderId, name: folder.name } : null}
          onClose={() => setManageSharingOpen(false)}
        />

        {/* Toast Notifications */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </div>
    </ThemeProvider>
  );
};

export default FolderFilesView;
